import type { GlSheetRowVO } from '@/services/accountService/glInvoiceService';
import type { MissingFsCodeEntry } from '@/utils/glToFsMapper';
import { isRateGlCode } from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';

export interface UnbalancedRowInfo {
    rowIndex: number;
    description: string;
    diff: number;
}

const BALANCE_TOLERANCE = 0.001;

export function isRateColumnKey(key: string): boolean {
    const glCode = key.includes('|') ? key.slice(key.indexOf('|') + 1) : key;
    return isRateGlCode(glCode);
}

/**
 * 行内借贷合计（HKD 等价值），应为 0。
 * 跳过 Ex-rate 列，以及调用方传入的排除列（外币原币等参考列，与 FS/银行汇总一致）。
 */
export function computeRowBalance(
    columnValues: Record<string, number> | undefined,
    excludeKeys?: Set<string>,
): number {
    if (!columnValues) return 0;
    let total = 0;
    for (const [key, val] of Object.entries(columnValues)) {
        if (isRateColumnKey(key)) continue;
        if (excludeKeys?.has(key)) continue;
        total += val ?? 0;
    }
    return total;
}

export function findUnbalancedRows(
    rows: GlSheetRowVO[],
    excludeKeys?: Set<string>,
): UnbalancedRowInfo[] {
    return rows
        .map((row, rowIndex) => {
            const diff = computeRowBalance(row.columnValues, excludeKeys);
            if (Math.abs(diff) <= BALANCE_TOLERANCE) return null;
            const description = row.description?.trim()
                || row.voucherNo?.trim()
                || row.date?.trim()
                || `Row ${rowIndex + 1}`;
            return { rowIndex, description, diff };
        })
        .filter((x): x is UnbalancedRowInfo => x !== null);
}

/** Submit 硬拦截：空数据、fsCode 无法映射到 FS */
export function validateGlSubmitHard(params: {
    totalRow: number;
    missingFsCodes: MissingFsCodeEntry[];
}): string[] {
    const errors: string[] = [];
    if (params.totalRow === 0) {
        errors.push('No GL transactions to submit.');
    }
    if (params.missingFsCodes.length > 0) {
        const details = params.missingFsCodes
            .map((m) => `${m.fsCode} (current: ${m.currentAmount}, prior: ${m.priorAmount})`)
            .join('; ');
        errors.push(`Cannot map GL to FS — missing FS categories: ${details}`);
    }
    return errors;
}
